import * as Sentry from '@sentry/react';
import { useEleosAuth } from '@common-ui-library/eleos-login';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AxiosInstance } from 'axios';
import { useBIEvents, SettingsProvider, logger } from '@common-ui-library/shared';
import { isEleosAdmin } from '../Utils/utils';
import { ROLES } from '../Utils/constants';
import { createApiClient, getExtraHeaders } from '../Utils/client';
import { UserRepository } from '../Utils/UserRepository';
import { API_SERVER, ELEOS_ENV } from '../Utils/config';
import { BI_LOGIN_SUCCESS, BI_LOGOUT } from '../Utils/biEvents';

export interface Session {
  user: {
    name?: string;
    email?: string;
    id?: string;
    organization?: string;
    [ROLES.ELEOS_ADMINPORTAL_ADMIN]?: boolean;
  };
}

export type AuthErrorType = 'session_expired' | 'no_permission' | 'no_site' | 'server_error' | null;

interface SessionContextType {
  session: Session | null;
  userManagementClient: AxiosInstance | null;
  authError: AuthErrorType;
  isPermissionChecking: boolean;
  onLogout: () => void;
  retryPermissionCheck: () => void;
}

const SessionContext = React.createContext<SessionContextType | undefined>(
  undefined
);

export const SessionContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user, logout, isLoading, getAccessToken } = useEleosAuth();
  const { sendBIEvent } = useBIEvents();
  const [sessionState, setSessionState] = useState<Session | null>(null);
  const [authError, setAuthError] = useState<AuthErrorType>(null);
  const [isPermissionChecking, setIsPermissionChecking] = useState(true); // Start checking

  // Single Axios client for User Management API - shared across permission checks and downstream providers
  const userManagementClient = useMemo(() => {
    if (!user || !getAccessToken) {
        return null;
    }
    const extraHeaders = getExtraHeaders(ELEOS_ENV);

    return createApiClient(
      API_SERVER + '/api/v1/usermanagement/',
      getAccessToken,
      extraHeaders
    );
  }, [user, getAccessToken]);

  // UserRepository for permission check (getMe)
  const userRepository = useMemo(() => {
    if (!userManagementClient) return null;
    return new UserRepository(userManagementClient);
  }, [userManagementClient]);

  const checkPermissions = useCallback(async () => {
    // Reset States
    setAuthError(null);
    setSessionState(null);

    if (!user) {
      setIsPermissionChecking(false);
      return;
    }

    if (!userRepository) {
       // If we have a user but no client/repo yet, we should probably KEEP checking (loading)
       // rather than giving up, unless we know for sure we failed.
       return; 
    }

    try {
      setIsPermissionChecking(true);
      // Fetch "Me" from Backend
      const { data: userData } = await userRepository.getMe();
      
      // Use Backend Roles as Source of Truth
      const roles = userData?.roles || user.roles || [];
      
      const session = {
        user: {
          name: userData?.name || user.name,
          email: userData?.email || user.email,
          id: userData?.id || user.id,
          organization: userData?.organization?.name || user.organization?.name,
          [ROLES.ELEOS_ADMINPORTAL_ADMIN]: isEleosAdmin(roles),
        },
      };

      // Set Sentry user context for error tracking
      Sentry.setUser({ email: session.user.email });

      if (!session.user[ROLES.ELEOS_ADMINPORTAL_ADMIN]) {
        setAuthError('no_permission');
      } else if (!userData?.organization) {
        setAuthError('no_site');
      }

      setSessionState(session);
      
      // Emit login_success BI event
      sendBIEvent(BI_LOGIN_SUCCESS, {
        email: session.user.email,
        organization: session.user.organization,
      });
    } catch (error: any) {
      const status = error.response?.status;
      const auth0Error = error.error; // Auth0 SDK errors have this property
      logger.error('Permission check failed', {
        httpStatus: status,
        auth0Error: auth0Error,
        errorMessage: error.message,
        errorName: error.name,
        // Include full error for debugging
        rawError: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      });
      
      // Session expired: 401 from server OR Auth0 token refresh failure
      if (status === 403) {
        setAuthError('no_permission');
      } else {
        setAuthError('session_expired');
      }
      // TODO - once we log and understand the explicit session_expired error, we can add a generic error handler here.
      setSessionState(null);
    } finally {
      setIsPermissionChecking(false);
    }
  }, [user, userRepository]);

  // Reactive session state - updates when user changes (login/logout)
  useEffect(() => {
    if (!isLoading) {
      checkPermissions();
    }
  }, [isLoading, checkPermissions, user]);

  // Manual logout - triggers Auth0 logout, cleanup happens reactively via useEffect above
  const onLogout = () => {
    // Emit logout BI event before logout
    sendBIEvent(BI_LOGOUT, {
      email: sessionState?.user?.email,
    });
    logout(); 
  };

  const sessionContextValue = useMemo(
    () => ({
      session: sessionState,
      userManagementClient,
      authError,
      isPermissionChecking,
      onLogout,
      retryPermissionCheck: checkPermissions,
    }),
    [
      sessionState,
      userManagementClient,
      authError,
      isPermissionChecking,
      onLogout,
      checkPermissions,
    ]
  );

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <SettingsProvider
        therapist={{
          username: sessionState?.user?.email || '',
          id: sessionState?.user?.id || '',
          organization: sessionState?.user?.organization || '',
        }}
        apiGatewayBaseUrl={API_SERVER}
      >
        {children}
      </SettingsProvider>
    </SessionContext.Provider>
  );
};


export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within an SessionContext');
  }
  return context;
};
